import React, { ReactElement, useState, useEffect } from 'react';
import { Hub } from 'aws-amplify';
import { useHistory } from 'react-router-dom';
import { Button } from '../molecules';
import { validateEmail, validatePassword } from '../../utils/validators';
import errorStyling from '../../utils/error-styling';
import { signUp } from '../../utils/auth-functions';
import openEyes from '../../assets/images/open-eyes.svg';
import closeEyes from '../../assets/images/close-eyes.svg';
type Props = {
  inApp: string | null;
  redirectUrl: string | null;
};

export default function SignUpForm({ inApp, redirectUrl }: Props): ReactElement {
  // const [isRendered, setRendered ] = useState<boolean>(false);
  // useEffect(() => setRendered(true), []);
  const [showPwd, setShowPwd] = useState<boolean>(false);
  const [formValidated, setFormValidated] = useState<number>(0);
  const [name, setName] = useState<string | boolean | undefined>(undefined);
  const [familyName, setFamilyName] = useState<string | boolean | undefined>(undefined);
  const [nickname, setNickname] = useState<string | boolean | undefined>();

  const [email, setEmail] = useState<string | undefined>(undefined);
  const [emailIsValid, setEmailIsValid] = useState<boolean | undefined>(undefined);

  const [passwordIsValid, setPasswordIsValid] = useState<boolean | undefined>(true);
  const [passwordIsMatching, setPasswordIsMatching] = useState<boolean | undefined>(undefined);
  const [password, setPassword] = useState<string | undefined>(undefined);
  const [confirmPasswordIsValid, setConfirmPasswordIsValid] = useState<boolean | undefined>(true);
  const [confirmPassword, setConfirmPassword] = useState<string | undefined>(undefined);
  const [privacyCheck, setPrivacyCheck] = useState<boolean>(true);

  const [error, setError] = useState<string | undefined>(undefined);
  const history = useHistory();

  const editPassword = (val: string | undefined) => {
    setPasswordIsValid(true);
    setPasswordIsMatching(undefined);
    setPassword(val);
  };

  const editConfirmPassword = (val: string | undefined) => {
    setConfirmPasswordIsValid(true);
    setPasswordIsMatching(undefined);
    setConfirmPassword(val);
  };

  const isFormValid = () => {
    return [name, familyName, emailIsValid, nickname, passwordIsValid, confirmPasswordIsValid, passwordIsMatching].reduce((acc, c) => !!acc && c, true) as boolean;
  };

  const validateForm = () => {
    setEmailIsValid(!!email && validateEmail({ email }) === 'valid');
    setPasswordIsValid(!!password && validatePassword({ password }) === 'valid');
    setConfirmPasswordIsValid(!!confirmPassword && validatePassword({ password: confirmPassword }) === 'valid');
    setPasswordIsMatching(!!password && !!confirmPassword && password === confirmPassword);
    if (!name) setName(false);
    if (!familyName) setFamilyName(false);
    if (!nickname) setNickname(false);
    return setFormValidated(new Date().getTime());
  };

  const getConfirmedUserLink = () => {
    const params = new URLSearchParams({
      inApp: String(inApp === 'true'),
      ...(redirectUrl ? { redirectUrl } : {}),
    });
    return ['/confirm-user', params.toString()].join('?');
  };

  const confirmUserLink = getConfirmedUserLink();

  useEffect(() => {
    if (formValidated > 0) {
      if (!isFormValid()) {
        setError("C'è un errore nei dati inseriti");
      } else {
        const signUpUser = {
          username: email || '',
          password: password || '',
          email: email || '',
          name: typeof name === 'string' ? name : '',
          surname: typeof familyName === 'string' ? familyName : '',
          nickname: typeof nickname === 'string' ? nickname : '',
          privacy: privacyCheck ? '1' : '0',
        };
        signUp(signUpUser);
      }
    }
  }, [formValidated]);

  useEffect(() => {
    /* later */
    const hubListenerCancelToken = Hub.listen('auth', (res) => {
      if (res?.payload?.data?.code) {
        setError(res.payload.data.code);
      } else {
        // const targetEmail = res.payload.data.user.username;
        setError(undefined);
        history.replace(confirmUserLink);
      }
    });
    return () => hubListenerCancelToken();
  }, []);

  const handleRedirectPolicyPrivacy = () => {
    window.open('https://www.sergiobonelli.it/bonelli-digital-comics/2022/09/23/news/privacy-policy-1022152/', '_blank');
  };

  return (
    <form id="signup-form">
      <div className="row">
        <div className="col-sm-12">
          <div className="form-group">
            <input onChange={(e) => setName(e.target.value)} type="text" name="name" className="form-control" placeholder="Nome (*)" />
          </div>
        </div>
        {name === false && <div className="input-not-valid mb-1">Inserire un nome</div>}

        <div className="col-sm-12">
          <div className="form-group">
            <input onChange={(e) => setFamilyName(e.target.value)} type="text" name="family_name" className="form-control" placeholder="Cognome (*)" />
          </div>
        </div>
      </div>
      {familyName === false && <div className="input-not-valid mb-1">Inserire un cognome</div>}

      <div className="row">
        <div className="col-sm-12">
          <div className="form-group">
            <input onChange={(e) => setEmail(e.target.value)} type="email" name="username" className="form-control" placeholder="Email (*)" />
          </div>
          {emailIsValid !== undefined && !emailIsValid && <div className="input-not-valid mb-2">Email non valida!</div>}
        </div>

        <div className="col-sm-12">
          <div className="form-group">
            <input onChange={(e) => setNickname(e.target.value)} type="nickname" name="nickname" className="form-control" placeholder="Nickname (*)" />
          </div>
        </div>
      </div>
      {nickname === false && <div className="input-not-valid mb-1">Inserire un Nickname</div>}

      <div className="row mt-4">
        <div className="col-sm-12 pwd">
          <img
            onClick={() => {
              setShowPwd(!showPwd);
            }}
            src={showPwd ? closeEyes : openEyes}
            className="eyes signUp"
          />
          <div className="form-group">
            <input onChange={(e) => editPassword(e.target.value)} type={showPwd ? 'text' : 'password'} id="password" name="password" className="form-control" placeholder="Password (*)" autoComplete="new-password" />
          </div>
          {passwordIsValid === false && (
            <div className="input-not-valid mb-2">
              Password non valida!
              <br /> Assicurati di aver inserito almeno 8 caratteri di cui un carattere speciale, maiuscolo, minuscolo ed un numero.
            </div>
          )}
        </div>

        <div className="col-sm-12">
          <div className="form-group">
            <input onChange={(e) => editConfirmPassword(e.target.value)} type={showPwd ? 'text' : 'password'} name="password_confirmation" className="form-control" placeholder="Conferma password (*)" autoComplete="new-password" />
          </div>
          {passwordIsMatching === false && <div className="input-not-valid mb-2">Le password non corrispondono.</div>}
          {confirmPasswordIsValid === false && (
            <div className="input-not-valid mb-2">
              Password non valida!
              <br /> Assicurati di aver inserito almeno 8 caratteri di cui un carattere speciale, maiuscolo, minuscolo ed un numero.
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 mb-3">
        <span role="button" onClick={() => handleRedirectPolicyPrivacy()}>
          L’utente acconsente al trattamento dei propri dati personali per ricevere con modalità cartacee e/o automatizzate comunicazioni promozionali e commerciali e/o newsletter relative a prodotti/servizi offerti ed iniziative del titolare del trattamento.
        </span>

        <br />
        <div className="mt-2">
          <div className="d-inline-flex gap-2">
            <input
              className="form-check-input mr-2"
              type="checkbox"
              checked={privacyCheck}
              onChange={() => setPrivacyCheck(!privacyCheck)}
            />
            <span>{'Acconsento'}</span>
          </div>
          <br />
          <div className="d-inline-flex gap-2">
            <input
              className="form-check-input mr-2"
              type="checkbox"
              checked={!privacyCheck}
              onChange={() => setPrivacyCheck(!privacyCheck)}
            />
            <span>{'Non acconsento'}</span>
          </div>
        </div>
      </div>
      <div className="mb-2">
        <ul>
          <li>
            <em>
              <small>I campi contrassegnati da (*) sono obbligatori</small>
            </em>
          </li>
          <li>
            <em>
              <small>La password deve essere lunga almeno 8 caratteri e contenere almeno un carattere maiuscolo, un carattere speciale, un numero</small>
            </em>
          </li>
        </ul>
      </div>
      {error !== undefined && <div className="input-not-valid text-center m-0">{errorStyling(error)}</div>}
      <div className="d-flex flex-direction-row justify-content-around my-3">
        {/* <Link to="/"><a className="btn override btn-secondary"rel="keep-params">Indietro</a></Link> */}
        <Button role="button" type={'submit'} action={() => validateForm()} className="pull-right btn override btn-primary">
          Registrati!
        </Button>
      </div>
    </form>
  );
}
